import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { BudgetProgress } from '../types';
import api from '../services/api';

const BudgetProgressOverview: React.FC = () => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const [progressData, setProgressData] = useState<BudgetProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);

  const fetchBudgetProgress = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/budget-progress/${selectedYear}/${selectedMonth}`);
      const data: BudgetProgress[] = response.data;
      setProgressData(data);
    } catch (error) {
      console.error(t('budget:progressFetchError'), error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgetProgress();
  }, [selectedYear, selectedMonth]);



  const getProgressText = (progress: number, type: 'income' | 'expense'): string => {
    if (type === 'income') {
      if (progress >= 100) return t('budget:goalAchieved');
      if (progress >= 80) return t('budget:onTrack');
      if (progress >= 60) return t('budget:attention');
      return t('budget:needsImprovement');
    } else {
      if (progress >= 100) return t('budget:budgetExceeded');
      if (progress >= 80) return t('budget:cautionNeeded');
      if (progress >= 60) return t('budget:attention');
      return t('budget:good');
    }
  };

  if (loading) {
    return (
      <div className="summary-card">
        <div className="loading">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="summary-card">
      <div className="list-header">
        <h2>{t('budget:thisMonthProgress')}</h2>
        <div style={{display: 'flex', gap: '10px'}}>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px'}}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
              <option key={year} value={year}>{year}{t('common:year')}</option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            style={{padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px'}}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
              <option key={month} value={month}>{month}{t('common:month')}</option>
            ))}
          </select>
        </div>
      </div>

      {progressData.length === 0 ? (
        <div className="empty-state">
          <p>{t('budget:noBudgetData')}</p>
          <p style={{fontSize: '14px', marginTop: '8px'}}>{t('budget:setBudgetInManagement')}</p>
        </div>
      ) : (
        <div style={{marginTop: '20px'}}>
          {progressData.map((item) => (
            <div key={item.categoryId} className="transaction-item" style={{marginBottom: '15px', display: 'block'}}>
              <div className="transaction-header">
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <span style={{fontWeight: 'bold', color: '#2c3e50'}}>{item.categoryName}</span>
                  <span className="transaction-category">
                    {item.categoryType === 'income' ? t('common:income') : t('common:expense')}
                  </span>
                </div>
                <div style={{textAlign: 'right'}}>
                  <div style={{fontSize: '14px', color: '#666'}}>
                    {formatCurrency(item.actualAmount)} / {formatCurrency(item.budgetAmount)}
                  </div>
                  <div style={{
                    fontSize: '14px', 
                    fontWeight: 'bold',
                    color: item.categoryType === 'income'
                      ? (item.progressRate ?? 0) >= 100 ? '#27ae60' : '#e74c3c'
                      : (item.progressRate ?? 0) >= 100 ? '#e74c3c' : '#27ae60'
                  }}>
                    {(item.progressRate ?? 0).toFixed(1)}% ({getProgressText(item.progressRate ?? 0, item.categoryType)})
                  </div>
                </div>
              </div>
              
              <div style={{width: '100%', backgroundColor: '#ecf0f1', borderRadius: '10px', height: '10px', marginTop: '15px'}}>
                <div
                  style={{
                    height: '10px',
                    borderRadius: '10px',
                    width: `${Math.min(item.progressRate ?? 0, 100)}%`,
                    backgroundColor: item.categoryType === 'income'
                      ? (item.progressRate ?? 0) >= 100 ? '#27ae60' : '#f39c12'
                      : (item.progressRate ?? 0) >= 100 ? '#e74c3c' : '#27ae60',
                  }}
                ></div>
              </div>
              
              {(item.progressRate ?? 0) > 100 && (
                <div style={{marginTop: '8px', fontSize: '14px', color: '#e74c3c'}}>
                  <span style={{fontWeight: 'bold'}}>
                    {item.categoryType === 'income' ? t('budget:goalExceededBy') : t('budget:budgetExceededBy')}
                    {formatCurrency(item.actualAmount - item.budgetAmount)}
                    {item.categoryType === 'income' ? t('budget:aboveGoal') : t('budget:exceededAmount')}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BudgetProgressOverview;